import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { GET_FILING_NOTIFICATIONS_FACILITY_CONTACTS } from "../../queries";
import { FilingNotificationsFormData } from "./types";

/**
 * Lets the user link an existing facility contact into the §13 emergency-contact
 * fields. Selecting a contact fills the free-text fields for display and sets
 * `emergencyContactPersonId`, which locks the fields (read-only) and makes save
 * link to that roster Person instead of materializing a copy. Clearing the link
 * (the "Use a different contact" affordance) returns the fields to manual entry.
 */
export function EmergencyContactPrefill({ facilityId }: { facilityId: string }) {
  const { setValue } = useFormContext<FilingNotificationsFormData>();
  const { data } = useQuery(GET_FILING_NOTIFICATIONS_FACILITY_CONTACTS, {
    variables: { facilityId },
    skip: !facilityId,
  });

  const options = useMemo(
    () =>
      (data?.facility?.contacts ?? []).map((contact) => ({
        id: contact.id,
        person: contact.person,
        label: [contact.person.first, contact.person.last].filter(Boolean).join(" ") || "(no name)",
      })),
    [data]
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <Autocomplete
      sx={{ minWidth: 240 }}
      size="small"
      options={options}
      value={null}
      blurOnSelect
      clearOnBlur
      getOptionLabel={(option) => option.label}
      onChange={(_, option) => {
        if (!option) {
          return;
        }
        // shouldValidate re-runs the compliance-engine resolver so prefilled
        // last name / phone clear their issues immediately — without it the
        // programmatic setValue updates the fields but never re-triggers the
        // onChange validation, leaving stale issues until an unrelated edit.
        const opts = { shouldDirty: true, shouldValidate: true } as const;
        setValue("emergencyContactFirst", option.person.first ?? null, opts);
        setValue("emergencyContactLast", option.person.last ?? null, opts);
        setValue("emergencyContactEmail", option.person.email ?? null, opts);
        setValue("emergencyContactPhone", option.person.phones?.[0]?.number ?? null, opts);
        // Linking to the roster Person locks the fields (read-only) and makes
        // save re-send the id instead of materializing a copy.
        setValue("emergencyContactPersonId", option.person.id ?? null, opts);
      }}
      renderInput={(params) => <TextField {...params} label="Prefill from contact" />}
    />
  );
}
